import { useState, useEffect, useRef } from "react";
import { useDialogFocus } from "../../../hooks/useDialogFocus";
import type { CrmTeamDetailResponse } from "../../../types/crm";
import { updateCrmTeam } from "../../../services/crmTeams";
import { SelectPopover } from "../../../components/SelectPopover";
import { ComboboxPopover } from "../../../components/ComboboxPopover";
import { ModalCloseButton } from "../../../components/ModalCloseButton";
import { US_STATES } from "../../../constants/usStates";
import { DIVISIONS } from "../../../constants/divisions";
import "../contacts/AddContactModal.css";

interface EditCrmTeamModalProps {
  isOpen: boolean;
  team: CrmTeamDetailResponse;
  onClose: () => void;
  onSuccess: (updated: CrmTeamDetailResponse) => void;
}

export const EditCrmTeamModal = ({ isOpen, team, onClose, onSuccess }: EditCrmTeamModalProps) => {
  const [name, setName] = useState("");
  const [division, setDivision] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [conference, setConference] = useState("");
  const [region, setRegion] = useState("");
  const [teamTwitter, setTeamTwitter] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !team) return;
    setName(team.name || "");
    setDivision(team.division || "");
    setState(team.state || "");
    setCity(team.city || "");
    setConference(team.conference || "");
    setRegion(team.region || "");
    setTeamTwitter(team.teamTwitter || "");
    setNotes(team.notes || "");
    setError("");
    setSaving(false);
  }, [isOpen, team]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !saving) onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, saving]);

  const dialogRef = useRef<HTMLFormElement>(null);

  useDialogFocus({ isOpen, dialogRef, handleEscape: false });

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("School name is required"); return; }

    setError("");
    setSaving(true);

    try {
      const updated = await updateCrmTeam(team.id, {
        name: name.trim(),
        division: division || undefined,
        state: state.trim() || undefined,
        city: city.trim() || undefined,
        conference: conference.trim() || undefined,
        region: region.trim() || undefined,
        teamTwitter: teamTwitter.trim() || undefined,
        notes: notes.trim() || undefined,
        defaultContactCoachId: team.defaultContactCoachId,
      });
      onSuccess(updated);
    } catch {
      setError("Failed to update school. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="add-contact-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form ref={dialogRef} tabIndex={-1} className="add-contact-modal" role="dialog" aria-modal="true" aria-label="Edit School" onSubmit={handleSubmit}>
        <div className="add-contact-modal__header">
          <h2 className="add-contact-modal__title">Edit School</h2>
          <ModalCloseButton onClick={onClose} disabled={saving} />
        </div>

        <div className="add-contact-modal__body">
          <div className="add-contact-modal__field">
            <label htmlFor="edit-crm-team-modal-school-name-0" className="add-contact-modal__label">School Name *</label>
            <input id="edit-crm-team-modal-school-name-0"
              className="add-contact-modal__input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Stanford"
              required
            />
          </div>

          <div className="add-contact-modal__row">
            <div className="add-contact-modal__field">
              <label htmlFor="edit-crm-team-modal-division-0" className="add-contact-modal__label">Division</label>
              <SelectPopover id="edit-crm-team-modal-division-0" options={DIVISIONS} value={division} onChange={setDivision} placeholder="Select..." fullWidth />
            </div>
            <div className="add-contact-modal__field">
              <label htmlFor="edit-crm-team-modal-state-1" className="add-contact-modal__label">State</label>
              <ComboboxPopover id="edit-crm-team-modal-state-1" options={US_STATES} value={state} onChange={setState} placeholder="e.g. CA" transform={(v) => v.toUpperCase()} fullWidth />
            </div>
          </div>

          <div className="add-contact-modal__row">
            <div className="add-contact-modal__field">
              <label htmlFor="edit-crm-team-modal-city-1" className="add-contact-modal__label">City</label>
              <input id="edit-crm-team-modal-city-1"
                className="add-contact-modal__input"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Palo Alto"
              />
            </div>
            <div className="add-contact-modal__field">
              <label htmlFor="edit-crm-team-modal-conference-2" className="add-contact-modal__label">Conference</label>
              <input id="edit-crm-team-modal-conference-2"
                className="add-contact-modal__input"
                type="text"
                value={conference}
                onChange={(e) => setConference(e.target.value)}
                placeholder="e.g. Pac-12"
              />
            </div>
          </div>

          <div className="add-contact-modal__row">
            <div className="add-contact-modal__field">
              <label htmlFor="edit-crm-team-modal-region-3" className="add-contact-modal__label">Region</label>
              <input id="edit-crm-team-modal-region-3"
                className="add-contact-modal__input"
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g. West"
              />
            </div>
            <div className="add-contact-modal__field">
              <label htmlFor="edit-crm-team-modal-team-twitter-4" className="add-contact-modal__label">Team Twitter</label>
              <input id="edit-crm-team-modal-team-twitter-4"
                className="add-contact-modal__input"
                type="text"
                value={teamTwitter}
                onChange={(e) => setTeamTwitter(e.target.value)}
                placeholder="@handle"
              />
            </div>
          </div>

          <div className="add-contact-modal__field">
            <label htmlFor="edit-crm-team-modal-notes-5" className="add-contact-modal__label">Notes</label>
            <textarea id="edit-crm-team-modal-notes-5"
              className="add-contact-modal__input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {error && <div className="add-contact-modal__error">{error}</div>}
        </div>

        <div className="add-contact-modal__footer">
          <button type="button" className="add-contact-modal__cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="add-contact-modal__save" disabled={saving || !name.trim()}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};
